import { ParsedUrlQuery } from 'querystring';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetStaticProps, GetStaticPaths } from 'next';
import Link from 'next/link';
import cn from 'classnames';
import retry from 'async-retry';

import { Icon, Image } from 'components';
import { Button, Input } from 'components/form';
import { getLayout } from 'components/layouts/Header';
import { Row, Col } from 'components/layout';
import MultiSelect from 'components/MultiSelect';
import SingleSelect from 'components/SingleSelect';
import ResponsiveCard from 'components/ResponsiveCard';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import {
  useBasicStudentSocieties,
  getInitialFilterParams,
  UniversityOption,
} from 'hooks/useBasicStudentSocieties';
import { API_ROUTES } from 'lib/api-routes';
import { PAGE_ROUTES } from 'lib/page-routes';
import {
  UniversitiesResponse,
  UniversityResponse,
} from 'lib/models/university';
import { get } from 'lib/utils/http';

import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import {
  BasicStudentSocietiesResponse,
  PER_PAGE_COUNT,
} from 'lib/models/basic-student-society';
import { pageBuilderInstance } from 'lib/utils/axios';
import { asyncRetryParams } from 'lib/consts';

import styles from './Societies.module.scss';

interface Params extends ParsedUrlQuery {
  universitySlug: string;
}

interface SocietiesProps {
  initialUniversity: UniversityOption;
  initialSocieties: BasicStudentSocietiesResponse;
  canonicalURL?: string;
}

const Societies = ({
  initialUniversity,
  initialSocieties,
  canonicalURL,
}: SocietiesProps): ReactElement => {
  const router = useRouter();
  const [university, setUniversity] = useState(initialUniversity);
  const useBottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setUniversity(initialUniversity);
  }, [initialUniversity]);

  const {
    societies,
    topics,
    setTopicSlugs,
    topicSlugs,
    resetFilter,
    universities,
    universityMap,
    search,
    setSearch,
    isLoading,
    isFilterApplied,
    size,
    setSize,
    hasMoreRecords,
    totalRecords,
  } = useBasicStudentSocieties({
    universitySlug: university.value,
    initialSocieties,
  });

  const nextPage = useCallback(() => {
    if (
      !isLoading &&
      hasMoreRecords &&
      societies.length === size * PER_PAGE_COUNT
    ) {
      setSize(size + 1);
    }
  }, [size, societies, hasMoreRecords, isLoading]);

  useVisibleOnIntersect({
    elRef: useBottomRef,
    threshold: 0,
    onIntersect: isLoading ? undefined : nextPage,
    rootMargin: '250px',
  });

  const pageTitle = `Clubs & Societies at ${university.label}`;
  const pageDescription = `View all Clubs & Societies at  ${university.label} and filter by topics to find your favourites! Click on their profiles to discover more.`;

  const onUniversityChange = (universitySlug: string) => {
    const pathname = `${PAGE_ROUTES.UNIVERSITIES}/${universitySlug}${PAGE_ROUTES.SOCIETIES}`;
    router.push(
      {
        pathname,
      },
      undefined,
      { shallow: false }
    );
    const university = universityMap[universitySlug];
    setUniversity(university);
  };

  const onTopicsChange = (topics: Array<string>) => {
    setTopicSlugs(topics);
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {university.metaKeywords && (
          <meta name="keywords" content={university.metaKeywords} />
        )}
        {canonicalURL ? <link rel="canonical" href={canonicalURL} /> : null}
      </Head>
      <section className={styles.container} data-testid="Societies">
        <main className={styles.main}>
          <header className={styles.header}>
            <Col sm={24}>
              <h1 className={styles.title}>Student {pageTitle}</h1>
            </Col>
            <Row
              align="center"
              justify="space-between"
              className={styles.descriptionReset}
            >
              <Col>
                <p className={styles.description}>
                  {`Total ${totalRecords} ${
                    totalRecords === 1 ? 'Society' : 'Societies'
                  } Listed`}
                </p>
              </Col>
              <Col>
                <Button
                  className={cn(styles.filterReset, styles.filterResetSmall)}
                  size="small"
                  color="danger"
                  variant="outlined"
                  borderless
                  onClick={resetFilter}
                  disabled={!isFilterApplied}
                  startIcon="reset"
                  iconSize="xsmall"
                >
                  Reset Filter
                </Button>
              </Col>
              <Col xs={24} md={6}>
                <Input
                  className={styles.search}
                  placeholder="Search Student Societies"
                  startIcon="search"
                  startIconSize="small"
                  endIconSize="xxsmall"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  endIcon={search ? 'cancel' : undefined}
                  onEndIconClick={() => setSearch('')}
                  endIconClassName={styles.searchClear}
                />
              </Col>
            </Row>
            <Row className={styles.filter} justify="space-between">
              <Row align="center" className={styles.filterGroup}>
                <Row>
                  <Icon iconName="filter" className={styles.filterIcon} />
                  <p>Filter By:</p>
                </Row>
                <SingleSelect
                  placeholder="Search university"
                  title={university.label}
                  options={universities}
                  value={university.value}
                  onChange={onUniversityChange}
                  alignment="left"
                />
                <MultiSelect
                  className={styles.filterTopics}
                  title="Topic"
                  placeholder="Search topics"
                  options={topics}
                  values={topicSlugs}
                  onChange={onTopicsChange}
                  alignment="left"
                  isSearchable
                />
              </Row>
              <Col>
                <Button
                  className={styles.filterReset}
                  size="small"
                  color="danger"
                  variant="outlined"
                  borderless
                  onClick={resetFilter}
                  disabled={!isFilterApplied}
                  startIcon="reset"
                  iconSize="xsmall"
                >
                  Reset Filter
                </Button>
              </Col>
            </Row>
          </header>
          <Row
            gutter={[
              { md: 12, lg: 20, xl: 32 },
              { md: 12, lg: 20, xl: 32 },
            ]}
          >
            {societies.map((society) => {
              const { id, icon_url, name, slug } = society;
              return (
                <Col
                  key={id}
                  sm={24}
                  md={8}
                  lg={6}
                  xxl={5}
                  className={styles.card}
                >
                  <Link
                    href={`${PAGE_ROUTES.SOCIETIES}/${slug}`}
                    prefetch={false}
                  >
                    <a>
                      <ResponsiveCard
                        title={name}
                        image={icon_url}
                        description={university.label}
                        descriptionIcon={university.avatar!}
                      />
                    </a>
                  </Link>
                </Col>
              );
            })}
          </Row>
          {!isLoading && societies.length === 0 && (
            <Row className={styles.noResults} justify="center">
              <Col>
                <Image
                  width="163"
                  height="100"
                  src="desert.png"
                  alt="No Results Found"
                  source="aws"
                />
                <p>No Results Found!</p>
                {isFilterApplied && (
                  <span>Refresh the filters and try again</span>
                )}
              </Col>
            </Row>
          )}
        </main>
        <div ref={useBottomRef} className={styles.bottomElement}></div>
      </section>
    </>
  );
};

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const { data } = await pageBuilderInstance<UniversitiesResponse>(
    API_ROUTES.UNIVERSITIES
  );
  const universities = parseArrayResponse(data);

  const paths = universities.map(({ slug }) => {
    return {
      params: { universitySlug: slug },
    };
  });
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<SocietiesProps, Params> = async (
  context
) => {
  const { universitySlug } = context.params!;
  let propsData;

  try {
    propsData = await retry(async () => {
      const { data: universityResponse } =
        await pageBuilderInstance<UniversityResponse>(
          `${API_ROUTES.UNIVERSITIES}/${universitySlug}`
        );
      const university = parseResponse(universityResponse);

      const tags = parseArrayResponse(university.meta_tags);
      const keywords = tags.map((tag) => tag.value).join(', ');

      const initialUniversity = {
        id: university.id,
        value: university.slug,
        label: university.name,
        avatar: university.icon_url,
        metaTitle: university.meta_title,
        metaDescription: university.meta_description,
        metaKeywords: keywords,
      };

      const filter = getInitialFilterParams(universitySlug);
      const pathname = `${API_ROUTES.STUDENT_SOCIETIES}?${filter}`;

      const initialSocieties = await get<BasicStudentSocietiesResponse>(
        pathname
      );

      const canonicalURL = `${process.env.NEXT_PUBLIC_SITE_URL}/universities/${university.slug}/societies`;

      return {
        initialUniversity,
        initialSocieties,
        canonicalURL,
      };
    }, asyncRetryParams);
  } catch {
    return { notFound: true };
  }

  return {
    props: propsData,
    revalidate: 3600,
  };
};

// @ts-ignore
Societies.getLayout = getLayout;

export default Societies;
